import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api, { SESSION_EXPIRED } from '../utils/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('erp_user')); } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('erp_token');
    if (token) {
      api.get('/auth/me')
        .then(({ data }) => { if (data?.user) { setUser(data.user); localStorage.setItem('erp_user', JSON.stringify(data.user)); } })
        // A token the server rejects is no token at all. The cached user was
        // being kept here, which let a dead session render the whole app.
        .catch((err) => { if (err.response?.status === 401) setUser(null); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Any request answered with a 401 ends the session everywhere at once — the
  // guards then send whoever is looking at a page to the login screen, keeping
  // where they were so they land back on it.
  useEffect(() => {
    const onExpired = () => {
      setUser((prev) => {
        if (prev) toast.error('Your session expired — please sign in again.');
        return null;
      });
    };
    window.addEventListener(SESSION_EXPIRED, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED, onExpired);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem('erp_token', data.token);
    localStorage.setItem('erp_user', JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem('erp_token');
    localStorage.removeItem('erp_user');
    setUser(null);
  };

  // Permission check mirroring the backend RBAC.
  const can = (perm) => {
    const perms = user?.permissions || [];
    if (perms.includes('*') || perms.includes(perm)) return true;
    const [mod] = perm.split(':');
    return perms.includes(`${mod}:*`);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, can }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
